import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, SquaresFour, UsersThree, Clock, CalendarDots, Fingerprint, Money,
  ClipboardText, FileText, Bell, ChartBar, GearSix, CreditCard, HandCoins, Medal,
  Question, User, UserGear, CheckCircle,
} from "@phosphor-icons/react";

function ModuleCard({ icon: Icon, name, desc, actions, testid }) {
  return (
    <div className="border border-border rounded-sm bg-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2.5">
        <Icon size={20} className="text-primary shrink-0" />
        <h3 className="font-heading font-bold text-foreground">{name}</h3>
      </div>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</p>
      {actions?.length > 0 && (
        <ul className="mt-3 space-y-1.5 border-t border-border pt-3">
          {actions.map(([btn, que]) => (
            <li key={btn} className="text-sm flex gap-2 leading-relaxed">
              <span className="shrink-0 font-medium text-foreground">{btn}:</span>
              <span className="text-muted-foreground">{que}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RoleIntro({ icon: Icon, title, children }) {
  return (
    <div className="mb-6 flex items-start gap-3 border border-border rounded-sm bg-accent/40 p-4">
      <Icon size={22} className="text-primary shrink-0 mt-0.5" />
      <div>
        <div className="font-heading font-bold text-foreground">{title}</div>
        <p className="text-sm text-muted-foreground leading-relaxed mt-1">{children}</p>
      </div>
    </div>
  );
}

const EMP_MODULES = [
  {
    icon: SquaresFour, name: "Panel", testid: "manual-mod-emp-panel",
    desc: "Tu pantalla de inicio: un resumen rápido de tu información, los cumpleaños próximos del equipo y la asistencia del día.",
  },
  {
    icon: Fingerprint, name: "Asistencia", testid: "manual-mod-emp-asistencia",
    desc: "Aquí registras tu hora de entrada y salida cada día. Si tu empresa activó la restricción por red, solo podrás marcar conectado a la WiFi autorizada.",
    actions: [
      ["Marcar entrada", "Registra tu hora de llegada. Hazlo al iniciar tu jornada."],
      ["Marcar salida", "Registra tu hora de salida al terminar."],
      ["Historial", "Tabla con tus marcaciones para que verifiques tus horas."],
    ],
  },
  {
    icon: ClipboardText, name: "Solicitudes", testid: "manual-mod-emp-solicitudes",
    desc: "Pide vacaciones o permisos y dale seguimiento a su estado.",
    actions: [
      ["Nueva solicitud", "Elige el tipo (vacaciones o permiso), las fechas y el motivo. Tu solicitud queda pendiente de aprobación."],
      ["Estado", "Cada solicitud muestra si está pendiente, aprobada o rechazada, con el comentario del administrador."],
    ],
  },
  {
    icon: FileText, name: "Documentos", testid: "manual-mod-emp-documentos",
    desc: "Contratos, constancias y memorandos que la empresa ha generado para ti. Toca un documento para verlo y descargarlo en PDF.",
  },
  {
    icon: Bell, name: "Notificaciones", testid: "manual-mod-emp-notificaciones",
    desc: "Mensajes y avisos de la empresa. Toca una notificación para marcarla como leída; si tiene archivos adjuntos, puedes descargarlos desde ahí.",
  },
];

const ADMIN_MODULES = [
  {
    icon: SquaresFour, name: "Panel", testid: "manual-mod-adm-panel",
    desc: "Visión general de la empresa: totales de empleados, cumpleaños próximos, quiénes marcaron asistencia hoy y una gráfica del neto pagado por planilla.",
  },
  {
    icon: UsersThree, name: "Empleados", testid: "manual-mod-adm-empleados",
    desc: "El directorio de tu personal. Todo empieza aquí: sin empleados no hay horarios, asistencia ni planillas.",
    actions: [
      ["Nuevo empleado", "Registra los datos personales, puesto y salario."],
      ["Lápiz / interruptor", "Edita la ficha o activa/desactiva al empleado sin borrar su historial."],
      ["Credenciales", "Genera el usuario y contraseña con los que el empleado entra al portal."],
      ["Importar Excel / Plantilla", "Descarga la plantilla, llénala y sube muchos empleados de una vez."],
      ["Casillas de selección", "Selecciona varios empleados para eliminarlos en lote."],
    ],
  },
  {
    icon: Clock, name: "Turnos", testid: "manual-mod-adm-turnos",
    desc: "Define los turnos de trabajo (matutino, nocturno, etc.) con su horario, color, horas y días de descanso. Se reutilizan al armar los horarios.",
    actions: [
      ["Nuevo turno", "Nombre, hora de entrada/salida, color para identificarlo y días de descanso."],
      ["Día libre", "Marca un turno como día libre para descansos programados."],
    ],
  },
  {
    icon: CalendarDots, name: "Horarios", testid: "manual-mod-adm-horarios",
    desc: "La cuadrícula semanal empleado × día. Asigna turnos uno por uno o de forma masiva, y configura el horario fijo para los cálculos automáticos.",
    actions: [
      ["Tocar una celda", "Asigna, cambia o quita el turno de ese empleado en ese día."],
      ["Asignación masiva", "Elige turno, rango de fechas, días de la semana y varios empleados a la vez. Ideal para horarios fijos o rotativos."],
      ["Horario fijo semanal", "Define entrada/salida por día y tolerancia de tardanza. Con esto activo, el sistema calcula solas las horas extra y tardanzas."],
    ],
  },
  {
    icon: Fingerprint, name: "Asistencia", testid: "manual-mod-adm-asistencia",
    desc: "Controla las marcaciones de todo el personal y las herramientas de puntualidad.",
    actions: [
      ["Marcar por empleado", "Registra entrada/salida de un empleado (útil si olvidó marcar)."],
      ["Tabla de hoy / Resumen", "Revisa las marcaciones del día o un resumen por empleado en un rango de fechas."],
      ["Reporte de puntualidad", "Detalle día por día de tardanzas y horas extra de un empleado, con deducción calculada."],
      ["Enviar al empleado", "Manda ese reporte al correo del empleado."],
      ["Restricción por red WiFi", "Actívala y agrega las IPs autorizadas: los empleados solo podrán marcar desde esa red. \"Agregar esta red\" captura tu IP actual."],
    ],
  },
  {
    icon: Money, name: "Planillas", testid: "manual-mod-adm-planillas",
    desc: "El corazón del pago: genera planillas individuales o en lote con deducciones de ley (IHSS, RAP, ISR), préstamos y comisiones incluidos.",
    actions: [
      ["Nueva planilla", "Al elegir empleado y período, el sistema detecta horas extra y tardanzas desde la asistencia. Tú decides con los interruptores \"Aplicar horas extra\" y \"Descontar tardanzas\"."],
      ["Deducciones automáticas", "Activa o desactiva IHSS, RAP e ISR para esa planilla."],
      ["Préstamos y comisiones", "Aparecen pre-marcados los pendientes del empleado: la cuota del préstamo se rebaja y la comisión se suma como bonificación."],
      ["Generación en lote", "Crea la planilla de todos los empleados a la vez, con los mismos interruptores de cálculo."],
      ["Grupos por período", "La tabla agrupa las planillas; toca el encabezado del grupo para expandirlo."],
      ["Lápiz (corregir)", "Ajusta días, horas extra, bonos o deducciones de una planilla ya generada."],
      ["Avión (reenviar)", "Reenvía el comprobante (voucher) al correo del empleado, útil tras una corrección."],
      ["Vouchers / PDF", "Envía comprobantes por correo o descarga la planilla en PDF con logo y firmas."],
      ["Eliminar", "Borra la planilla y revierte automáticamente los abonos de préstamos y comisiones que aplicó."],
    ],
  },
  {
    icon: HandCoins, name: "Préstamos", testid: "manual-mod-adm-prestamos",
    desc: "Controla los préstamos a empleados y sus abonos. Las cuotas se descuentan solas en la planilla según la frecuencia (semanal, quincenal o mensual).",
    actions: [
      ["Nuevo préstamo", "Monto, interés opcional, cuota y frecuencia. El sistema calcula el total y el saldo."],
      ["Filtro por empleado", "Muestra los préstamos activos y la deuda pendiente de una persona."],
      ["Abonar", "Registra un pago manual fuera de planilla."],
      ["PDF", "Estado de cuenta del préstamo con abonos, saldo y líneas de firma."],
      ["Cancelar / Eliminar", "Cancela un préstamo (se conserva el historial) o elimínalo."],
    ],
  },
  {
    icon: Medal, name: "Comisiones", testid: "manual-mod-adm-comisiones",
    desc: "Bonificaciones por empleado que se acumulan durante el período y se suman automáticamente a la planilla.",
    actions: [
      ["Gestionar", "Abre el panel del empleado: agrega comisiones del período, edita o elimina las pendientes y revisa el historial de pagadas."],
      ["Acumulado pendiente", "Lo que se sumará como bonificación en la próxima planilla. Al generarla, las comisiones pasan a \"pagadas\"."],
    ],
  },
  {
    icon: ClipboardText, name: "Solicitudes", testid: "manual-mod-adm-solicitudes",
    desc: "Aprueba o rechaza las vacaciones y permisos que piden los empleados, con comentario. También puedes consultar y ajustar los saldos de vacaciones.",
  },
  {
    icon: FileText, name: "Documentos", testid: "manual-mod-adm-documentos",
    desc: "Genera contratos, memorandos y constancias con las plantillas de la empresa, y descárgalos en PDF con el logo. Puedes crear y editar tus propias plantillas.",
  },
  {
    icon: Bell, name: "Notificaciones", testid: "manual-mod-adm-notificaciones",
    desc: "Envía avisos a todo el personal o a un empleado específico, con archivos adjuntos de hasta 10 MB.",
  },
  {
    icon: ChartBar, name: "Reportes", testid: "manual-mod-adm-reportes",
    desc: "Descarga reportes en Excel con el logo de la empresa: asistencia, planillas, préstamos y comisiones. Filtra por rango de fechas o deja vacío para todo el historial.",
  },
  {
    icon: CreditCard, name: "Mi suscripción", testid: "manual-mod-adm-suscripcion",
    desc: "El estado del plan de tu empresa y su pago.",
    actions: [
      ["Elegir plan", "Compara planes y alterna entre pago mensual o anual."],
      ["Pagar con PayPal", "Completa el pago de forma segura; puedes aplicar un código de descuento."],
      ["Renovación automática", "Actívala para no perder el servicio al vencer el período."],
      ["Historial de pagos", "Descarga el comprobante PDF de cada pago."],
    ],
  },
  {
    icon: GearSix, name: "Configuración", testid: "manual-mod-adm-config",
    desc: "Datos de la empresa, parámetros de deducciones (IHSS, RAP, ISR), tarifas de horas extra y la imagen de marca: logo y color del portal.",
  },
];

const FAQ = [
  ["No puedo marcar mi asistencia", "Tu empresa activó la restricción por red: solo se puede marcar desde la WiFi autorizada. Conéctate a la red de la oficina o pide al administrador que agregue tu red con el botón \"Agregar esta red\"."],
  ["No me llegó el comprobante de planilla al correo", "Verifica con tu administrador que tu ficha de empleado tenga un correo registrado. El administrador puede reenviar el comprobante con el botón del avión en Planillas."],
  ["Veo el mensaje \"Tu empresa está suspendida\"", "La suscripción de la empresa venció o fue suspendida por la plataforma. El administrador de la empresa puede regularizar el pago desde \"Mi suscripción\"."],
  ["Olvidé mi contraseña", "En la pantalla de inicio de sesión toca \"¿Olvidaste tu contraseña?\": recibirás un código de 6 dígitos en tu correo para crear una nueva."],
  ["Las deducciones de la planilla no cuadran", "Revisa el interruptor \"Deducciones automáticas\" (IHSS, RAP, ISR) y los de horas extra/tardanzas. Si ya se generó, el administrador puede corregirla con el botón del lápiz y reenviar el comprobante."],
  ["¿Los préstamos se rebajan solos?", "Sí: al generar la planilla, la cuota se descuenta automáticamente si la frecuencia del préstamo coincide con el período (semanal, quincenal o mensual). La cuota nunca excede el saldo restante."],
  ["¿Las comisiones se pagan solas?", "Sí: las comisiones pendientes se acumulan y se suman como bonificación al generar la planilla; luego pasan al historial de pagadas."],
  ["¿La web y la app móvil usan los mismos datos?", "Sí, ambas están conectadas a la misma base de datos en tiempo real: lo que registres en una se refleja en la otra."],
  ["¿Cómo cambio entre modo claro y oscuro?", "Con el botón de sol/luna en la parte inferior del menú lateral."],
];

export default function Manual() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("resumen");

  useEffect(() => {
    if (user?.role === "admin") setTab("admin");
    else if (user?.role === "employee") setTab("empleado");
  }, [user]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="manual-page">
      <div className="mx-auto max-w-5xl p-4 sm:p-8">
        <div className="flex items-center justify-between gap-4">
          <div className="font-heading text-xl font-black tracking-tight">
            CAUS<span className="text-muted-foreground">·</span>RRHH
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground"
            onClick={() => navigate("/")}
            data-testid="manual-back-button"
          >
            <ArrowLeft className="mr-2" size={15} /> Volver al portal
          </Button>
        </div>

        <div className="mt-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight" data-testid="manual-title">
            Manual de uso
          </h1>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground leading-relaxed">
            Una guía rápida para entender qué hace cada botón del sistema. Elige la sección según tu rol.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto rounded-sm bg-muted border border-border">
            <TabsTrigger value="resumen" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="manual-tab-resumen">Resumen</TabsTrigger>
            <TabsTrigger value="empleado" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="manual-tab-empleado">Empleado</TabsTrigger>
            <TabsTrigger value="admin" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="manual-tab-admin">Administrador</TabsTrigger>
            <TabsTrigger value="faq" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="manual-tab-faq">Preguntas frecuentes</TabsTrigger>
          </TabsList>

          <TabsContent value="resumen" className="mt-6 space-y-4" data-testid="manual-resumen">
            <div className="border border-border rounded-sm bg-card p-5">
              <h2 className="font-heading text-base md:text-lg font-bold text-foreground">¿Qué es este portal?</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Es el portal web de CAUS - HR Suite: un sistema de recursos humanos conectado en tiempo real con la app móvil.
                Desde aquí se gestionan empleados, turnos, horarios, asistencia, planillas, préstamos, comisiones, solicitudes,
                documentos, notificaciones y reportes. Todo lo que registres en la web aparece en la app y viceversa.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="border border-border rounded-sm bg-card p-5" data-testid="manual-role-empleado">
                <User size={22} className="text-primary" />
                <h3 className="mt-2 font-heading font-bold text-foreground">Empleado</h3>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                  Marca su asistencia, pide vacaciones o permisos, y consulta sus documentos y notificaciones.
                </p>
              </div>
              <div className="border border-border rounded-sm bg-card p-5" data-testid="manual-role-admin">
                <UserGear size={22} className="text-primary" />
                <h3 className="mt-2 font-heading font-bold text-foreground">Administrador</h3>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                  Gestiona todo lo de su empresa: personal, horarios, asistencia, planillas, préstamos, comisiones y la suscripción.
                </p>
              </div>
            </div>
            <div className="border border-border rounded-sm bg-card p-5">
              <h2 className="font-heading text-base md:text-lg font-bold text-foreground">Primeros pasos</h2>
              <ul className="mt-3 space-y-2">
                {[
                  "Inicia sesión con tu usuario o correo y tu contraseña.",
                  "Si tu empresa aún no está registrada, usa la pestaña \"Nueva empresa\" en la pantalla de acceso.",
                  "Si olvidaste tu contraseña, usa el enlace \"¿Olvidaste tu contraseña?\" para recibir un código en tu correo.",
                  "Navega por los módulos desde el menú lateral izquierdo; en tu celular, ábrelo con el botón de lista.",
                  "Cambia entre modo claro y oscuro con el botón de sol/luna al final del menú.",
                ].map((paso, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-muted-foreground leading-relaxed">
                    <CheckCircle size={16} className="text-primary shrink-0 mt-0.5" />
                    {paso}
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="empleado" className="mt-6" data-testid="manual-empleado">
            <RoleIntro icon={User} title="Guía para empleados">
              Estos son los módulos que verás en tu menú lateral. Tu empresa te proporciona tu usuario y contraseña.
            </RoleIntro>
            <div className="grid gap-4 md:grid-cols-2">
              {EMP_MODULES.map((m) => <ModuleCard key={m.testid} {...m} />)}
            </div>
          </TabsContent>

          <TabsContent value="admin" className="mt-6" data-testid="manual-admin">
            <RoleIntro icon={UserGear} title="Guía para administradores de empresa">
              Como administrador tienes acceso a todos los módulos del portal. El orden sugerido para empezar:
              Empleados → Turnos → Horarios → Asistencia → Planillas.
            </RoleIntro>
            <div className="grid gap-4 md:grid-cols-2">
              {ADMIN_MODULES.map((m) => <ModuleCard key={m.testid} {...m} />)}
            </div>
          </TabsContent>

          <TabsContent value="faq" className="mt-6" data-testid="manual-faq">
            <RoleIntro icon={Question} title="Preguntas frecuentes">
              Los problemas más comunes y cómo resolverlos.
            </RoleIntro>
            <div className="space-y-3">
              {FAQ.map(([q, a], i) => (
                <div key={i} className="border border-border rounded-sm bg-card p-5" data-testid={`manual-faq-${i}`}>
                  <div className="flex items-start gap-2.5">
                    <Question size={18} className="text-primary shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-foreground text-sm">{q}</h3>
                      <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
